import React, { useState, useEffect, useCallback } from 'react';

import * as Page from 'components/page';
import { RHP } from 'providers';

export function StartupStep11({ useRankingStartup, t }) {
    const {
        vars,
        getStartupRankingApplication,
        postStartupAuditEnrollment,
    } = useRankingStartup;
    const { application } = vars;
    const { steps } = application;

    const enrollment = application?.auditEnrollment || { status: 'pending' };

    // Compute scaleup at view time from the same revenue/investment ranges
    // that step2 uses to drive its scaleup warning. Read straight from
    // `application` (the ranking-application entry) — step11 refreshes
    // that on focus/visibility via getStartupRankingApplication, while
    // startupDetails is not refreshed and would be stale right after the
    // user saved step2.
    const revenuesRange = application?.revenuesRange || [];
    const investmentRange = application?.investmentRange || [];
    const isScaleup =
        !!application?.forcedScaleup ||
        (Number(investmentRange[0]) === 0 &&
            Number(investmentRange[1]) >= 20000000) ||
        (Number(revenuesRange[0]) === 0 &&
            Number(revenuesRange[1]) >= 20000000) ||
        Number(revenuesRange[0]) >= 2500000 ||
        Number(investmentRange[0]) >= 2500000;

    const [submitting, setSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    const refresh = useCallback(() => {
        getStartupRankingApplication().catch(() => {});
    }, [getStartupRankingApplication]);

    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState === 'visible') refresh();
        };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', refresh);
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', refresh);
        };
    }, [refresh]);

    // Open Startup (early-stage) is treated as confirmed without ever hitting
    // the backend or persisting any auditEnrollment record. The home compute
    // (`!isScaleup || enrollment.status === 'confirmed'`) handles this for the
    // overall ranking checklist; here we just render the confirmed UI.

    if (!steps || steps.step10 !== true) {
        RHP.replace('/ranking/latam/startup/');
        return null;
    }

    const onClickEnroll = async () => {
        setSubmitting(true);
        setErrorMsg(null);
        try {
            const redirectUrl = window.location.href;
            const result = await postStartupAuditEnrollment({ redirectUrl });
            if (result?.paymentLink && result.status === 'processing') {
                window.location.href = result.paymentLink;
            }
        } catch (err: any) {
            console.error(err);
            setErrorMsg(
                err?.response?.data?.message ||
                err?.message ||
                t('StartupStep11.errorGeneric')
            );
        } finally {
            setSubmitting(false);
        }
    };

    const onClickReopenLink = () => {
        if (enrollment.paymentLink) {
            window.location.href = enrollment.paymentLink;
        }
    };

    const renderStatusEarlyStage = () => (
        <div style={{ padding: '1rem', background: '#e6f7ed', borderRadius: 4 }}>
            <strong>{t('StartupStep11.statusEarlyStageConfirmedTitle')}</strong>
            <p style={{ margin: '0.5rem 0 0' }}>
                {t('StartupStep11.statusEarlyStageConfirmedDescr')}
            </p>
        </div>
    );

    const renderStatusScaleup = () => {
        if (enrollment.status === 'confirmed') {
            return (
                <div style={{ padding: '1rem', background: '#e6f7ed', borderRadius: 4 }}>
                    <strong>{t('StartupStep11.statusScaleupConfirmedTitle')}</strong>
                    <p style={{ margin: '0.5rem 0 0' }}>
                        {t('StartupStep11.statusScaleupConfirmedDescr')}
                    </p>
                </div>
            );
        }
        if (enrollment.status === 'processing') {
            return (
                <div style={{ padding: '1rem', background: '#fff8e1', borderRadius: 4 }}>
                    <strong>{t('StartupStep11.statusProcessingTitle')}</strong>
                    <p style={{ margin: '0.5rem 0 0.75rem' }}>
                        {t('StartupStep11.statusProcessingDescr')}
                    </p>
                    {enrollment.paymentLink && (
                        <button
                            type="button"
                            onClick={onClickReopenLink}
                            className="btn btn-primary"
                        >
                            {t('StartupStep11.reopenPaymentLink')}
                        </button>
                    )}
                </div>
            );
        }
        if (enrollment.status === 'failed') {
            return (
                <div style={{ padding: '1rem', background: '#fdecea', borderRadius: 4 }}>
                    <strong>{t('StartupStep11.statusFailedTitle')}</strong>
                    <p style={{ margin: '0.5rem 0 0.75rem' }}>
                        {t('StartupStep11.statusFailedDescr')}
                    </p>
                    <button
                        type="button"
                        onClick={onClickEnroll}
                        disabled={submitting}
                        className="btn btn-primary"
                    >
                        {t('StartupStep11.tryAgain')}
                    </button>
                </div>
            );
        }
        return (
            <div style={{ padding: '1rem', background: '#f2f2f2', borderRadius: 4 }}>
                <strong>{t('StartupStep11.statusPendingTitle')}</strong>
                <p style={{ margin: '0.5rem 0 0.75rem' }}>
                    {t('StartupStep11.statusPendingDescr')}
                </p>
                {/* INICIO DO BOTAO PAGBANK */}
                <a
                    href="https://pag.ae/81MvGkVB7/button"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Pagar com PagBank"
                >
                    <img
                        src="https://assets.pagseguro.com.br/ps-integration-assets/botoes/pagamentos/205x30-pagar-azul.gif"
                        alt="Pague com PagBank - é rápido, grátis e seguro!"
                    />
                </a>
                {/* FIM DO BOTAO PAGBANK */}
            </div>
        );
    };

    return (
        <Page.Page
            name="StartupStep11"
            back="/ranking/latam/startup"
            customBackText={t('glossary:backToRanking')}
        >
            <Page.Title>{t('StartupStep11.title')}</Page.Title>
            <Page.Descr>{t('StartupStep11.description')}</Page.Descr>

            <p style={{ fontSize: '0.9em', color: '#666', marginTop: 12 }}>
                {t('StartupStep11.priceNote')}
            </p>

            <h3 style={{ marginTop: 24 }}>{t('StartupStep11.statusSectionTitle')}</h3>
            {isScaleup ? renderStatusScaleup() : renderStatusEarlyStage()}

            {errorMsg && (
                <p style={{ color: '#dc3545', marginTop: 12 }}>{errorMsg}</p>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
                <Page.Buttn
                    style={{ width: '20%' }}
                    // @ts-ignore
                    to="/ranking/latam/startup/step10"
                >
                    <span className="icon far fa-long-arrow-left" aria-hidden="true" />
                    <span className="text">{t('glossary:back')}</span>
                </Page.Buttn>

                <Page.Buttn
                    style={{ width: '80%' }}
                    // @ts-ignore
                    to="/ranking/latam/startup"
                >
                    <span className="text">{t('glossary:backToRanking')}</span>
                </Page.Buttn>
            </div>
        </Page.Page>
    );
}
